import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaTh, 
  FaList, 
  FaWifi,
  FaCogs,
  FaEye,
  FaNetworkWired,
  FaInfoCircle,
  FaRocket,
  FaBuilding,
  FaBroadcastTower
} from 'react-icons/fa';
import useScrollAnimation from '../../../hooks/useScrollAnimation';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const AccessPointWIFI = () => {
  const { t, lang } = useLanguage();

  // Fonction pour obtenir les produits avec TOUS les champs traduits (incluant technologies/options)
  const getWifiProducts = () => [
    {
      id: 1,
      name: "Cisco Meraki CW9172I",
      image: "/images/MirakiWIFI/cisco-cw9172i.jpg",
      category: "wifi7-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 7 tri-radio conçu pour les bureaux, retail et environnements de santé" : "Tri-radio WiFi 7 access point designed for offices, retail and healthcare environments",
      keyUse: lang === 'fr' ? "Bureaux d'entreprise, Retail, Santé, Hôtellerie" : "Enterprise Offices, Retail, Healthcare, Hospitality",
      options: lang === 'fr' ? "WiFi 7, Tri-radio, 6 flux spatiaux, BLE/IoT, Radio scan dédiée" : "WiFi 7, Tri-radio, 6 spatial streams, BLE/IoT, Dedicated scanning radio"
    },
    {
      id: 2,
      name: "Cisco Meraki CW9176I",
      image: "/images/MirakiWIFI/cisco-cw9176i.avif",
      category: "wifi7-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 7 haute performance 4x4:4 MU-MIMO sur 3 radios et 3 bandes" : "High-performance WiFi 7 access point 4x4:4 MU-MIMO on 3 radios and 3 bands",
      keyUse: lang === 'fr' ? "Environnements haute densité, Campus, Entreprises" : "High-density environments, Campus, Enterprises",
      options: lang === 'fr' ? "WiFi 7, 4x4:4 MU-MIMO, 3 bandes (2.4/5/6 GHz), 10 Gbps Ethernet" : "WiFi 7, 4x4:4 MU-MIMO, 3 bands (2.4/5/6 GHz), 10 Gbps Ethernet"
    },
    {
      id: 3,
      name: "Cisco Meraki CW9176D1",
      image: "/images/MirakiWIFI/cisco-cw9176d1.avif",
      category: "wifi7-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 7 haute performance avec antenne directionnelle intégrée" : "High-performance WiFi 7 access point with integrated directional antenna",
      keyUse: lang === 'fr' ? "Couverture directionnelle, Halls, Couloirs longs" : "Directional coverage, Halls, Long corridors",
      options: lang === 'fr' ? "WiFi 7, Antenne directionnelle, 4x4:4 MU-MIMO, 10 Gbps Ethernet" : "WiFi 7, Directional antenna, 4x4:4 MU-MIMO, 10 Gbps Ethernet"
    },
    {
      id: 4,
      name: "Cisco Meraki CW9178I",
      image: "/images/MirakiWIFI/cisco-cw9178i.avif",
      category: "wifi7-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 7 ultra-haute performance 4x4:4 MU-MIMO sur 4 radios" : "Ultra-high performance WiFi 7 access point 4x4:4 MU-MIMO on 4 radios",
      keyUse: lang === 'fr' ? "Environnements critiques, Data centers, Campus premium" : "Critical environments, Data centers, Premium campus",
      options: lang === 'fr' ? "WiFi 7, 4 radios, 3 bandes (2.4/5/5/6 GHz), Dual 10 Gbps Ethernet" : "WiFi 7, 4 radios, 3 bands (2.4/5/5/6 GHz), Dual 10 Gbps Ethernet"
    },
    {
      id: 5,
      name: "Cisco Meraki MR57",
      image: "/images/MirakiWIFI/cisco-mr57.avif",
      category: "wifi6e-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6E ultra-haute performance 4x4:4 MU-MIMO avec radio flex" : "Ultra-high performance WiFi 6E access point 4x4:4 MU-MIMO with flex radio",
      keyUse: lang === 'fr' ? "Bureaux haute densité, Campus, Environnements exigeants" : "High-density offices, Campus, Demanding environments",
      options: lang === 'fr' ? "WiFi 6E, 4x4:4 MU-MIMO, Radio flex, 7.78 Gbps tri-radio" : "WiFi 6E, 4x4:4 MU-MIMO, Radio flex, 7.78 Gbps tri-radio"
    },
    {
      id: 6,
      name: "Cisco Meraki CW9166",
      image: "/images/MirakiWIFI/cisco-cw9166.avif",
      category: "wifi6e-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6E ultra-haute performance 4x4:4 MU-MIMO avec radio flex" : "Ultra-high performance WiFi 6E access point 4x4:4 MU-MIMO with flex radio",
      keyUse: lang === 'fr' ? "Entreprises, Campus universitaires, Centres de conférence" : "Enterprises, University campuses, Conference centers",
      options: lang === 'fr' ? "WiFi 6E, 4x4:4 MU-MIMO, Radio flex, 7.8 Gbps tri-radio" : "WiFi 6E, 4x4:4 MU-MIMO, Flex radio, 7.8 Gbps tri-radio"
    },
    {
      id: 7,
      name: "Cisco Meraki CW9166D1",
      image: "/images/MirakiWIFI/cisco-cw9166d1.avif",
      category: "wifi6e-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6E ultra-haute performance avec antenne directionnelle intégrée" : "Ultra-high performance WiFi 6E access point with integrated directional antenna",
      keyUse: lang === 'fr' ? "Couverture ciblée, Environnements spécifiques" : "Targeted coverage, Specific environments",
      options: lang === 'fr' ? "WiFi 6E, Antenne directionnelle intégrée, 4x4:4 MU-MIMO, 7.8 Gbps" : "WiFi 6E, Integrated directional antenna, 4x4:4 MU-MIMO, 7.8 Gbps"
    },
    {
      id: 8,
      name: "Cisco Meraki CW9164",
      image: "/images/MirakiWIFI/cisco-cw9164.avif",
      category: "wifi6e-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6E haute performance 2x2:2 et 4x4:4 MU-MIMO compatible" : "High-performance WiFi 6E access point 2x2:2 and 4x4:4 MU-MIMO compatible",
      keyUse: lang === 'fr' ? "Bureaux moyens, PME, Espaces collaboratifs" : "Medium offices, SMB, Collaborative spaces",
      options: lang === 'fr' ? "WiFi 6E, 2x2:2 et 4x4:4 MU-MIMO, 7.5 Gbps tri-radio" : "WiFi 6E, 2x2:2 and 4x4:4 MU-MIMO, 7.5 Gbps tri-radio"
    },
    {
      id: 9,
      name: "Cisco Meraki CW9162",
      image: "/images/MirakiWIFI/cisco-cw9162.avif",
      category: "wifi6e-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6E polyvalent 2x2:2 MU-MIMO compatible" : "Versatile WiFi 6E access point 2x2:2 MU-MIMO compatible",
      keyUse: lang === 'fr' ? "Bureaux généraux, Retail, Usage standard" : "General offices, Retail, Standard use",
      options: lang === 'fr' ? "WiFi 6E, 2x2:2 MU-MIMO, 3.9 Gbps tri-radio" : "WiFi 6E, 2x2:2 MU-MIMO, 3.9 Gbps tri-radio"
    },
    {
      id: 10,
      name: "Cisco Meraki MR56",
      image: "/images/MirakiWIFI/cisco-mr56.avif",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 pour environnements de bureau et campus haute densité" : "WiFi 6 access point for high-density office and campus environments",
      keyUse: lang === 'fr' ? "Bureaux haute densité, Campus, Auditoriums" : "High-density offices, Campus, Auditoriums",
      options: lang === 'fr' ? "WiFi 6, 8x8:8 UL/DL MU-MIMO, OFDMA, 5 Gbps multigigabit" : "WiFi 6, 8x8:8 UL/DL MU-MIMO, OFDMA, 5 Gbps multigigabit"
    },
    {
      id: 11,
      name: "Cisco Meraki MR46",
      image: "/images/MirakiWIFI/cisco-mr46.avif",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 haute densité 4×4:4 UL/DL MU-MIMO et OFDMA" : "High-density WiFi 6 access point 4×4:4 UL/DL MU-MIMO and OFDMA",
      keyUse: lang === 'fr' ? "Bureaux, Écoles, Hôpitaux, Retail" : "Offices, Schools, Hospitals, Retail",
      options: lang === 'fr' ? "WiFi 6, 4×4:4 UL/DL MU-MIMO, OFDMA, 2.5 Gbps multigigabit" : "WiFi 6, 4×4:4 UL/DL MU-MIMO, OFDMA, 2.5 Gbps multigigabit"
    },
    {
      id: 12,
      name: "Cisco Meraki MR46E",
      image: "/images/MirakiWIFI/cisco-mr46e.avif",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 haute densité 4×4:4 MU-MIMO avec antennes externes" : "High-density WiFi 6 access point 4×4:4 MU-MIMO with external antennas",
      keyUse: lang === 'fr' ? "Environnements industriels, Entrepôts, Grandes surfaces" : "Industrial environments, Warehouses, Large stores",
      options: lang === 'fr' ? "WiFi 6, 4×4:4 MU-MIMO, OFDMA, Antennes externes multigigabit" : "WiFi 6, 4×4:4 MU-MIMO, OFDMA, Multigigabit external antennas"
    },
    {
      id: 13,
      name: "Cisco Meraki MR44",
      image: "/images/MirakiWIFI/cisco-mr44.avf",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 haute densité 2×2:2+4×4:4 UL/DL MU-MIMO et OFDMA" : "High-density WiFi 6 access point 2×2:2+4×4:4 UL/DL MU-MIMO and OFDMA",
      keyUse: lang === 'fr' ? "Bureaux moyens, Espaces collaboratifs, PME" : "Medium offices, Collaborative spaces, SMB",
      options: lang === 'fr' ? "WiFi 6, 2×2:2+4×4:4 UL/DL MU-MIMO, OFDMA, 2.5 Gbps multigigabit" : "WiFi 6, 2×2:2+4×4:4 UL/DL MU-MIMO, OFDMA, 2.5 Gbps multigigabit"
    },
    {
      id: 14,
      name: "Cisco Meraki MR36",
      image: "/images/MirakiWIFI/cisco-mr36.avif",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 haute performance 2×2:2 UL/DL MU-MIMO et OFDMA" : "High-performance WiFi 6 access point 2×2:2 UL/DL MU-MIMO and OFDMA",
      keyUse: lang === 'fr' ? "Usage général, Petits bureaux, Espaces communs" : "General use, Small offices, Common areas",
      options: lang === 'fr' ? "WiFi 6, 2×2:2 UL/DL MU-MIMO, OFDMA, Usage polyvalent" : "WiFi 6, 2×2:2 UL/DL MU-MIMO, OFDMA, Versatile use"
    },
    {
      id: 15,
      name: "Cisco Meraki MR36H",
      image: "/images/MirakiWIFI/cisco-mr36h.avif",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 pour expériences invités intelligentes avec switch 4 ports intégré" : "WiFi 6 access point for smart guest experiences with integrated 4-port switch",
      keyUse: lang === 'fr' ? "Hôtellerie, Retail, Expérience client, Guest WiFi" : "Hospitality, Retail, Customer experience, Guest WiFi",
      options: lang === 'fr' ? "WiFi 6, 2×2:2 MU-MIMO, Switch 4 ports gigabit intégré" : "WiFi 6, 2×2:2 MU-MIMO, Integrated 4-port gigabit switch"
    },
    {
      id: 16,
      name: "Cisco Meraki MR28",
      image: "/images/MirakiWIFI/cisco-mr28.avif",
      category: "wifi6-indoor",
      description: lang === 'fr' ? "Point d'accès WiFi 6 couverture basique intérieure 2x2:2 MU-MIMO" : "Basic indoor WiFi 6 coverage access point 2x2:2 MU-MIMO",
      keyUse: lang === 'fr' ? "Couverture basique, Petits espaces, Budget serré" : "Basic coverage, Small spaces, Tight budget",
      options: lang === 'fr' ? "WiFi 6, 2x2:2 MU-MIMO, 1.5 Gbps aggregate, Économique" : "WiFi 6, 2x2:2 MU-MIMO, 1.5 Gbps aggregate, Economical"
    },
    {
      id: 17,
      name: "Cisco Meraki CW9163E",
      image: "/images/MirakiWIFI/cisco-cw9163e.avif",
      category: "outdoor",
      description: lang === 'fr' ? "Point d'accès extérieur WiFi 6E haute performance 2x2:2 MU-MIMO" : "High-performance outdoor WiFi 6E access point 2x2:2 MU-MIMO",
      keyUse: lang === 'fr' ? "Campus extérieur, Parking, Zones industrielles" : "Outdoor campus, Parking, Industrial zones",
      options: lang === 'fr' ? "WiFi 6E, 2x2:2 MU-MIMO, 3.9 Gbps tri-radio, Ruggedisé" : "WiFi 6E, 2x2:2 MU-MIMO, 3.9 Gbps tri-radio, Ruggedized"
    },
    {
      id: 18,
      name: "Cisco Meraki MR86",
      image: "/images/MirakiWIFI/cisco-mr86.avif",
      category: "outdoor",
      description: lang === 'fr' ? "Point d'accès extérieur haute densité ruggedisé 4×4:4 MU-MIMO et OFDMA" : "High-density ruggedized outdoor access point 4×4:4 MU-MIMO and OFDMA",
      keyUse: lang === 'fr' ? "Stades, Événements extérieurs, Campus large" : "Stadiums, Outdoor events, Large campus",
      options: lang === 'fr' ? "WiFi 6, 4×4:4 MU-MIMO, OFDMA, Antennes externes multigigabit" : "WiFi 6, 4×4:4 MU-MIMO, OFDMA, Multigigabit external antennas"
    },
    {
      id: 19,
      name: "Cisco Meraki MR78",
      image: "/images/MirakiWIFI/cisco-mr78.avif",
      category: "outdoor",
      description: lang === 'fr' ? "Point d'accès extérieur ruggedisé WiFi 6 basique 2x2:2 MU-MIMO" : "Basic ruggedized outdoor WiFi 6 access point 2x2:2 MU-MIMO",
      keyUse: lang === 'fr' ? "Couverture extérieure basique, Parkings, Zones périphériques" : "Basic outdoor coverage, Parkings, Peripheral zones",
      options: lang === 'fr' ? "WiFi 6, 2x2:2 MU-MIMO, 1.5 Gbps aggregate, Ruggedisé" : "WiFi 6, 2x2:2 MU-MIMO, 1.5 Gbps aggregate, Ruggedized"
    },
    {
      id: 20,
      name: "Cisco Meraki MR76",
      image: "/images/MirakiWIFI/cisco-mr76.avif",
      category: "outdoor",
      description: lang === 'fr' ? "Point d'accès extérieur haute performance ruggedisé 2×2:2 MU-MIMO et OFDMA" : "High-performance ruggedized outdoor access point 2×2:2 MU-MIMO and OFDMA",
      keyUse: lang === 'fr' ? "Campus, Zones industrielles, Connecteurs antennes externes" : "Campus, Industrial zones, External antenna connectors",
      options: lang === 'fr' ? "WiFi 6, 2×2:2 MU-MIMO, OFDMA, Connecteurs antennes externes" : "WiFi 6, 2×2:2 MU-MIMO, OFDMA, External antenna connectors"
    }
  ];

  const WIFI_DATA = {
    categories: [
      { id: 'wifi7-indoor', name: t.wifi.categories.wifi7, description: t.wifi.categories.wifi7D, icon: FaWifi, color: '#dc2626' },
      { id: 'wifi6e-indoor', name: t.wifi.categories.wifi6e, description: t.wifi.categories.wifi6eD, icon: FaNetworkWired, color: '#0ea5e9' },
      { id: 'wifi6-indoor', name: t.wifi.categories.wifi6, description: t.wifi.categories.wifi6D, icon: FaBuilding, color: '#10b981' },
      { id: 'outdoor', name: t.wifi.categories.outdoor, description: t.wifi.categories.outdoorD, icon: FaBroadcastTower, color: '#f59e0b' }
    ]
  };

  const [filteredProducts, setFilteredProducts] = useState(getWifiProducts());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 6;

  const [advantagesRef] = useScrollAnimation();

  useEffect(() => {
    let filtered = getWifiProducts();
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.keyUse.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, lang]);

  const paginatedProducts = filteredProducts.slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const getCategoryInfo = (categoryId) => WIFI_DATA.categories.find(cat => cat.id === categoryId);
  const handleCategoryChange = (category) => { setSelectedCategory(category); setCurrentPage(1); };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.wifi.seo.title}</title>
        <meta name="description" content={t.wifi.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/wifi" />
        
        {/* Balises Open Graph pour les réseaux sociaux */}
        <meta property="og:title" content={t.wifi.seo.title} />
        <meta property="og:description" content={t.wifi.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/MirakiWIFI/WiFiImage.avif" />
        <meta property="og:type" content="product" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Hero Section */}
      <section style={{
        background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
        padding: '6rem 0 4rem 0', position: 'relative', overflow: 'hidden'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="text-center text-lg-start">
              <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                <Badge bg="danger" className="mb-3" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', borderRadius: '20px', textTransform: 'uppercase' }}>
                  <FaWifi className="me-2" /> {t.wifi.hero.badge}
                </Badge>
                <h1 dangerouslySetInnerHTML={{ __html: t.wifi.hero.title }} style={{ fontSize: '3.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.5rem', lineHeight: '1.2' }} />
                <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', marginBottom: '2rem', maxWidth: '600px' }}>{t.wifi.hero.subtitle}</p>
                <div className="d-flex flex-wrap gap-3">
                  <Button variant="danger" size="lg" onClick={() => document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' })} style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600 }}>
                    <FaEye className="me-2" /> {t.wifi.hero.btnExplore}
                  </Button>
                  <Button variant="outline-light" size="lg" onClick={() => window.open('https://meraki.cisco.com/products/wi-fi/', '_blank')} style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600 }}>
                    <FaInfoCircle className="me-2" /> {t.wifi.hero.btnGuide}
                  </Button>
                </div>
              </motion.div>
            </Col>
            <Col lg={4} className="text-center">
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2rem', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <img src="/images/MirakiWIFI/WiFiImage.avif" alt="WiFi" style={{ width: '300px', height: '180px', objectFit: 'cover', borderRadius: '16px' }} />
                <h4 style={{ color: '#fff', marginTop: '1rem' }}>{t.wifi.hero.cardTitle}</h4>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>{t.wifi.hero.cardSub}</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Section */}
      <section id="solutions-section" className="py-5">
        <Container>
          <Row>
            <Col lg={3}>
              <div style={{ background: '#fff', borderRadius: '20px', padding: '1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'sticky', top: '2rem' }}>
                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', display: 'block' }}><FaSearch className="me-2" /> {t.wifi.sidebar.search}</label>
                  <Form.Control type="text" placeholder={t.wifi.sidebar.searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ borderRadius: '12px' }} />
                </div>
                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', display: 'block' }}><FaFilter className="me-2" /> {t.wifi.sidebar.category}</label>
                  <div className="d-grid gap-2">
                    <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} size="sm" style={{ borderRadius: '10px', textAlign: 'left' }}>{t.wifi.sidebar.allCats}</Button>
                    {WIFI_DATA.categories.map(cat => (
                      <Button key={cat.id} variant={selectedCategory === cat.id ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange(cat.id)} size="sm" style={{ borderRadius: '10px', textAlign: 'left' }}><cat.icon className="me-2" /> {cat.name}</Button>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', display: 'block' }}>{t.wifi.sidebar.view}</label>
                  <div className="d-flex gap-2">
                    <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('grid')} size="sm" style={{ borderRadius: '10px', flex: 1 }}><FaTh /></Button>
                    <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('list')} size="sm" style={{ borderRadius: '10px', flex: 1 }}><FaList /></Button>
                  </div>
                </div>
                <div className="text-center p-2" style={{ background: '#f8f9fa', borderRadius: '12px' }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}><span style={{ color: '#dc2626' }}>{filteredProducts.length}</span> {t.wifi.sidebar.found}</p>
                </div>
              </div>
            </Col>

            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedProducts.map((product, index) => (
                    <Col md={6} xl={4} key={product.id}>
                      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                        <Card style={{ borderRadius: '20px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', overflow: 'hidden', height: '100%' }}>
                          <div style={{ height: '200px', background: `${getCategoryInfo(product.category)?.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <img src={product.image} alt={product.name} style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
                            <Badge style={{ position: 'absolute', top: '1rem', right: '1rem', backgroundColor: getCategoryInfo(product.category)?.color }}>{getCategoryInfo(product.category)?.name}</Badge>
                          </div>
                          <Card.Body style={{ padding: '1.5rem' }}>
                            <h5 style={{ fontWeight: 700, color: '#1e293b' }}>{product.name}</h5>
                            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>{product.description}</p>
                            <div className="mb-3" style={{ background: '#f8fafc', borderRadius: '12px', padding: '0.8rem', textAlign: 'center' }}>
                              <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>{t.wifi.card.usage}</div>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{product.keyUse}</div>
                            </div>
                            <h6 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4a5568', textTransform: 'uppercase' }}><FaCogs size={12} color="#dc2626" className="me-2" />{t.wifi.card.tech}</h6>
                            <div style={{ background: '#f1f5f9', borderRadius: '8px', padding: '0.75rem' }}>
                              {product.options.split(',').map((opt, idx) => (
                                <div key={idx} style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center' }}>
                                  <span style={{ width: '4px', height: '4px', backgroundColor: '#dc2626', borderRadius: '50%', marginRight: '0.5rem' }} /> {opt.trim()}
                                </div>
                              ))}
                            </div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="d-grid gap-4">
                  {paginatedProducts.map((product, index) => (
                    <motion.div key={product.id} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
                      <Card style={{ borderRadius: '20px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <Row className="g-0">
                          <Col md={4} style={{ background: `${getCategoryInfo(product.category)?.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={product.image} alt={product.name} style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} /></Col>
                          <Col md={8}>
                            <Card.Body style={{ padding: '2rem' }}>
                              <h4 style={{ fontWeight: 700 }}>{product.name}</h4>
                              <p style={{ color: '#64748b' }}>{product.description}</p>
                              <Row>
                                <Col sm={6}><small style={{ color: '#64748b', textTransform: 'uppercase' }}>{t.wifi.card.usage}</small><div style={{ fontWeight: 600 }}>{product.keyUse}</div></Col>
                                <Col sm={6}><small style={{ color: '#64748b', textTransform: 'uppercase' }}>{t.wifi.card.tech}</small><div style={{ background: '#f8fafc', padding: '0.5rem', borderRadius: '6px' }}>{product.options.split(',').map((opt, i) => (<div key={i} style={{ fontSize: '0.8rem' }}>• {opt.trim()}</div>))}</div></Col>
                              </Row>
                            </Card.Body>
                          </Col>
                        </Row>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5 gap-2">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Button key={i + 1} variant={currentPage === i + 1 ? "danger" : "outline-danger"} onClick={() => setCurrentPage(i + 1)} style={{ borderRadius: '50%', width: '40px', height: '40px', fontWeight: 600 }}>{i + 1}</Button>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Avantages Section */}
      <section ref={advantagesRef} style={{ padding: '8rem 0', background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/datacenter2.jpg')`, backgroundSize: 'cover', backgroundAttachment: 'fixed', position: 'relative' }}>
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Col lg={12} className="text-center mb-5">
            <h2 dangerouslySetInnerHTML={{ __html: t.wifi.advantages.title }} style={{ fontSize: '2.8rem', fontWeight: 700, color: '#ffffff' }} />
            <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', maxWidth: '700px', margin: '0 auto' }}>{t.wifi.advantages.subtitle}</p>
          </Col>
          <Row className="g-4">
            <Col lg={6}>
              <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%' }}>
                <div style={{ background: 'rgba(220,38,38,0.1)', borderRadius: '16px', width: '70px', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2rem' }}><FaNetworkWired style={{ fontSize: '2rem', color: '#dc2626' }} /></div>
                <h3 style={{ color: '#1a202c', fontWeight: 700, fontSize: '1.3rem' }}>{t.wifi.advantages.card1Title}</h3>
                <p style={{ color: '#4a5568' }}>{t.wifi.advantages.card1Desc}</p>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%' }}>
                <div style={{ background: 'rgba(220,38,38,0.1)', borderRadius: '16px', width: '70px', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2rem' }}><FaRocket style={{ fontSize: '2rem', color: '#dc2626' }} /></div>
                <h3 style={{ color: '#1a202c', fontWeight: 700, fontSize: '1.3rem' }}>{t.wifi.advantages.card2Title}</h3>
                <p style={{ color: '#4a5568', marginBottom: '2rem' }}>{t.wifi.advantages.card2Desc}</p>
                {t.wifi.advantages.card2List.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626', marginRight: '1rem', marginTop: '0.65rem' }} /><span style={{ fontSize: '0.9rem', color: '#475569' }}>{f}</span></div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default AccessPointWIFI;